export type GeneratedDocumentFormat = "pdf" | "xlsx";
export type GeneratedDocumentGfmHeaderMode = "render" | "omit";

export interface GeneratedDocumentArtifact {
  fileId: number;
  filename: string;
  sizeBytes?: number;
  format: GeneratedDocumentFormat;
  markdown?: string;
  title?: string;
  styledTable?: true;
  gfmHeaderMode?: GeneratedDocumentGfmHeaderMode;
}

export interface GeneratedDocumentRename {
  fileId: number;
  filename: string;
  format: GeneratedDocumentFormat;
}

export const generatedDocumentFallbackPayload = (
  artifact: GeneratedDocumentArtifact
): {
  markdown: string;
  title: string;
  output_format?: GeneratedDocumentFormat;
  gfm_header_mode?: GeneratedDocumentGfmHeaderMode;
  filename?: string;
} => {
  const title =
    artifact.title ||
    artifact.filename.replace(/\.(?:pdf|xlsx)$/i, "") ||
    "Neo Document";
  return {
    markdown: artifact.markdown || "",
    title,
    ...(artifact.styledTable
      ? {
          output_format: artifact.format,
          ...(artifact.gfmHeaderMode
            ? { gfm_header_mode: artifact.gfmHeaderMode }
            : {}),
          filename: artifact.filename,
        }
      : {}),
  };
};

const GENERATED_DOCUMENT_FORMATS: Readonly<
  Record<string, GeneratedDocumentFormat>
> = {
  generate_pdf_document: "pdf",
  generate_daily_clinical_briefing_for_clinic: "pdf",
  generate_daily_clinical_briefing_v2_for_clinic: "pdf",
  generate_daily_clinical_briefing_v3_for_clinic: "pdf",
  generate_excel_document: "xlsx",
};

const positiveInteger = (value: unknown): number | undefined =>
  typeof value === "number" && Number.isInteger(value) && value > 0
    ? value
    : undefined;

const canonicalDocumentFormat = (
  value: unknown
): GeneratedDocumentFormat | undefined =>
  value === "pdf" || value === "xlsx" ? value : undefined;

const documentUriMatchesFile = (
  value: unknown,
  fileId: number | undefined
): boolean => {
  if (!fileId || typeof value !== "string") return false;
  const uri = value.trim();
  return (
    uri === `neo-contextual-file:${fileId}` ||
    uri.endsWith(`/documents/${fileId}/content`)
  );
};

export const generatedDocumentRenameFromToolCall = (toolCall: {
  toolName: string;
  status: string;
  isError?: boolean;
  output?: unknown;
}): GeneratedDocumentRename | null => {
  const output =
    toolCall.output && typeof toolCall.output === "object"
      ? (toolCall.output as Record<string, unknown>)
      : undefined;
  const automaticRename =
    output?.automatic_action === "renamed_existing_byte_identical_artifact";
  if (
    (toolCall.toolName !== "rename_generated_document" && !automaticRename) ||
    toolCall.status !== "completed" ||
    toolCall.isError ||
    !output
  ) {
    return null;
  }
  const fileId = positiveInteger(output.file_id);
  const format = output.output_format;
  if (
    !fileId ||
    typeof output.filename !== "string" ||
    !output.filename.trim() ||
    (format !== "pdf" && format !== "xlsx")
  ) {
    return null;
  }
  return { fileId, filename: output.filename.trim(), format };
};

/**
 * Turn a successful document-generation tool call into the small, safe subset
 * the chat UI needs to render a download card.
 */
export const generatedDocumentArtifactFromToolCall = (toolCall: {
  toolName: string;
  status: string;
  isError?: boolean;
  input?: unknown;
  output?: unknown;
}): GeneratedDocumentArtifact | null => {
  if (toolCall.status !== "completed" || toolCall.isError) return null;
  if (!toolCall.output || typeof toolCall.output !== "object") return null;

  const output = toolCall.output as Record<string, unknown>;
  if (
    output.deduplicated === true &&
    output.automatic_action === "renamed_existing_byte_identical_artifact"
  ) {
    return null;
  }
  const input =
    toolCall.input && typeof toolCall.input === "object"
      ? (toolCall.input as Record<string, unknown>)
      : undefined;
  const styledTable = toolCall.toolName === "generate_styled_table_document";
  const declaredFormat = output.output_format ?? input?.output_format;
  const fileId = positiveInteger(output.file_id);
  const canonicalFormat = canonicalDocumentFormat(output.output_format);
  const canonicalArtifact =
    canonicalFormat &&
    documentUriMatchesFile(output.document_uri, fileId) &&
    typeof output.filename === "string" &&
    Boolean(output.filename.trim());
  const format = styledTable
    ? canonicalDocumentFormat(declaredFormat)
    : GENERATED_DOCUMENT_FORMATS[toolCall.toolName] ||
      (canonicalArtifact ? canonicalFormat : undefined);
  if (!format) return null;
  if (
    !fileId ||
    typeof output.filename !== "string" ||
    !output.filename.trim()
  ) {
    return null;
  }

  const markdown =
    typeof input?.markdown === "string" && input.markdown.trim()
      ? input.markdown
      : undefined;
  const title =
    typeof input?.title === "string" && input.title.trim()
      ? input.title.trim()
      : undefined;
  const declaredHeaderMode = output.gfm_header_mode ?? input?.gfm_header_mode;
  const gfmHeaderMode =
    styledTable &&
    (declaredHeaderMode === "render" || declaredHeaderMode === "omit")
      ? declaredHeaderMode
      : undefined;

  return {
    fileId,
    filename: output.filename.trim(),
    sizeBytes: positiveInteger(output.size_bytes),
    format,
    markdown,
    title,
    ...(styledTable ? { styledTable: true as const } : {}),
    ...(gfmHeaderMode ? { gfmHeaderMode } : {}),
  };
};
